import Button from '@/components/Button.jsx';
import FloatingSelectV2 from '@/components/v2/FloatingSelectV2.jsx';
import { FiEye, FiPlus, LuPencil } from '@/components/react-icons/main';
import Stack from '@/components/v3/Stack.jsx';
import setPath from '@/lib/setPath.js';
import { setModalState } from '@/redux/actions/uiActions/modalActions.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors.js';
import memoizeSelector from '@/redux/lib/memoizeSelector.js';
import selectors from '@/redux/selectors/selectors.js';
import openWorkflowLogsModal from '@/screens/workflows/lib/openWorkflowLogsModal.js';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import workflowScreenSelectors from '../redux/selectors/workflowScreenSelectors.js';
import WorkflowPublishButton from './WorkflowScreenActions/WorkflowPublishButton.jsx';
import WorkflowReturnToPublishedButton from './WorkflowScreenActions/WorkflowReturnToPublishedButton.jsx';
import WorkflowStatusButton from './WorkflowScreenActions/WorkflowStatusButton.jsx';
import WorkflowTestCredentialsButton from './WorkflowScreenActions/WorkflowTestCredentialsButton.jsx';
import WorkflowVersionButton from './WorkflowScreenActions/WorkflowVersionButton.jsx';
import useResponsive from '@/hooks/useResponsive.js';
import WorkflowStageButton from './WorkflowScreenActions/WorkflowStageButton.jsx';

const WorkflowScreenActions = () => {
  const workflowId = useArgSelector(selectors.param, 'workflowId');
  const isPublishedAndEditable = useSelector(workflowScreenSelectors.activeWorkflowIsPublishedAndEditable);
  const workflowMetadataPublishedVersion = useSelector((state) => selectors.workflowMetadataById(state, workflowId)?.publishedVersion);
  const workflowLogsQuery = useArgSelector(selectors.query, 'workflowLogs');
  const workflowStagesIsEnabled = useSelector((state) => selectors.activeCompany(state)?.settings?.workflowStages === true);
  const isAdmin = useSelector(selectors.admin);
  const workflowName = useSelector((state) => workflowScreenSelectors.activeWorkflow(state)?.name);
  const workflowIssueContext = useSelector(
    memoizeSelector(`workflowIssueContext:${workflowId}`, (state) => {
      if (!workflowId) {
        return null;
      }

      const projects = selectors.companyIntegrationsProjects(state) || [];
      for (const project of projects) {
        const workflows = project?.workflows;
        if (!Array.isArray(workflows)) {
          continue;
        }

        const matchedWorkflow = workflows.find((workflow) => workflow?.id === workflowId);
        if (matchedWorkflow) {
          const projectId = matchedWorkflow?.projectId || project?.projectId || project?.id || null;
          return {
            issueId: matchedWorkflow?.issueId || null,
            projectId,
            requirements: matchedWorkflow?.requirements,
            workflowName: matchedWorkflow?.name
          };
        }
      }

      return null;
    })
  );

  const { tablet } = useResponsive();

  const showEdit = isPublishedAndEditable && !!workflowMetadataPublishedVersion;
  const shouldShowRequirements = isAdmin && !!workflowIssueContext?.issueId;

  const requirementsOptions = useMemo(
    () => [
      { value: 'view', label: 'View Requirements', icon: FiEye },
      { value: 'request', label: 'Request Additional Requirements', icon: FiPlus }
    ],
    []
  );

  const handleEdit = () => {
    setPath(`/workflows/${workflowId}/draft`, { keepSearch: true });
  };

  const handleViewRequirements = () => {
    if (!workflowIssueContext?.issueId) {
      return;
    }

    setModalState('workflowBlueprint', {
      open: true,
      workflowId,
      issueId: workflowIssueContext.issueId,
      projectId: workflowIssueContext.projectId,
      requirements: workflowIssueContext.requirements,
      workflowName: workflowIssueContext.workflowName || workflowName
    });
  };

  const handleRequirementsChange = (option) => {
    if (!option?.value) {
      return;
    }

    if (option.value === 'view') {
      handleViewRequirements();
      return;
    }

    if (option.value === 'request') {
      if (!workflowIssueContext?.issueId) {
        return;
      }

      setModalState('requestAdditionalRequirements', {
        open: true,
        workflowId,
        issueId: workflowIssueContext.issueId,
        projectId: workflowIssueContext.projectId,
        requirements: workflowIssueContext.requirements,
        workflowName: workflowIssueContext.workflowName || workflowName
      });
    }
  };

  useEffect(() => {
    if (workflowLogsQuery === 'true') {
      setModalState('workflowLogs', { open: true });
    }
  }, [workflowLogsQuery]);

  return (
    <Stack direction={'horizontal'} className="workflow-screen-actions" gap={6}>
      <div className="workflow-publish-container">
        <WorkflowReturnToPublishedButton />
        <WorkflowStatusButton />
        <WorkflowVersionButton />
      </div>
      {workflowStagesIsEnabled && <WorkflowStageButton />}
      {shouldShowRequirements && (
        <>
          <Button className="requirements-button outline">Requirements</Button>
          <FloatingSelectV2 triggers={['.requirements-button']} options={requirementsOptions} onChange={handleRequirementsChange} useSearch={false} placement="bottom-start" />
        </>
      )}
      <Button onClick={openWorkflowLogsModal} className="logs-button outline">
        {tablet ? 'Logs' : 'View Logs'}
      </Button>
      {showEdit && (
        <Button className="primary publish-button" icon={LuPencil} onClick={handleEdit}>
          {tablet ? 'Edit' : 'Edit Draft'}
        </Button>
      )}
      <WorkflowTestCredentialsButton />
      <WorkflowPublishButton />
    </Stack>
  );
};

export default WorkflowScreenActions;
